import type { VendorPeopleScope, VendorRemovalTarget } from '@/services/VendorService'
import type { VendorService as VendorServiceValue } from '@lib/enums/vendor-service.enum'
import type { VendorDirectoryQuery } from '@lib/input/vendor-directory.input'

/**
 * TanStack Query key factories. Centralizing keys here keeps cache reads and
 * invalidations consistent — a mutation invalidates `venueKeys.lists()` and
 * every list query rooted at it is refreshed, with no stringly-typed drift.
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  /**
   * One entry per listing: the filters and the page are both part of what was
   * asked for, so both are part of the key. Without them, a filtered page would
   * be written over an unfiltered one and the two would swap at random.
   */
  list: (query?: object, page?: number) =>
    [...userKeys.lists(), query ?? 'all', page ?? 'all'] as const,
  managedVenues: (userId: string) => [...userKeys.all, 'managed-venues', userId] as const,
  /** The company's roles, and which the session may give: what the role picker offers. */
  assignableRoles: () => [...userKeys.all, 'assignable-roles'] as const,
}

/** Venues the current session may switch into (not the scoped venues list). */
export const switchableVenueKeys = {
  all: ['switchable-venues'] as const,
  list: () => [...switchableVenueKeys.all, 'list'] as const,
}

export const venueKeys = {
  all: ['venues'] as const,
  lists: () => [...venueKeys.all, 'list'] as const,
  /**
   * One entry per page. Without the page in the key, page 2's rows would be
   * written over page 1's under the same key and the two would swap at random.
   * `'all'` is the unpaged listing, which is still what most callers ask for.
   */
  list: (page?: number) => [...venueKeys.lists(), page ?? 'all'] as const,
  details: () => [...venueKeys.all, 'detail'] as const,
  detail: (id: string) => [...venueKeys.details(), id] as const,
  version: (venueId: string, spaceId: string, versionId: string) =>
    [...venueKeys.all, 'version', venueId, spaceId, versionId] as const,
  managers: (venueId: string) => [...venueKeys.detail(venueId), 'managers'] as const,
  /** Which unconfirmed plans follow one version of a room. Under `all`, so version edits refresh it. */
  followers: (venueId: string, spaceId: string, versionId: string) =>
    [...venueKeys.all, 'version', venueId, spaceId, versionId, 'followers'] as const,
  /** Not venue-specific: the zones the API accepts are the same for every venue. */
  timezones: () => [...venueKeys.all, 'timezones'] as const,
}

export const furnitureKeys = {
  all: ['furniture'] as const,
  lists: () => [...furnitureKeys.all, 'list'] as const,
  /**
   * One entry per page. Without the page in the key, page 2's rows would be
   * written over page 1's under the same key and the two would swap at random.
   * `'all'` is the unpaged listing, which is still what most callers ask for.
   */
  list: (page?: number) => [...furnitureKeys.lists(), page ?? 'all'] as const,
  /** Where one piece is in use; refreshed whenever the catalog is. */
  usage: (id: string) => [...furnitureKeys.all, 'usage', id] as const,
}

export const furnitureGroupKeys = {
  all: ['furniture-groups'] as const,
  lists: () => [...furnitureGroupKeys.all, 'list'] as const,
  /** Keyed by venue for the same reason as `furnitureKeys.list`. */
  list: (venueId?: string | null) => [...furnitureGroupKeys.lists(), venueId ?? 'all'] as const,
}

export const venueLayoutKeys = {
  all: ['venue-layouts'] as const,
  space: (venueId: string, spaceId: string) => [...venueLayoutKeys.all, venueId, spaceId] as const,
}

export const tenantKeys = {
  all: ['tenants'] as const,
  lists: () => [...tenantKeys.all, 'list'] as const,
  /** One entry per page — see the note on `venueKeys.list`. */
  list: (page?: number) => [...tenantKeys.lists(), page ?? 'all'] as const,
  details: () => [...tenantKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenantKeys.details(), id] as const,
  users: (tenantId: string) => [...tenantKeys.all, tenantId, 'users'] as const,
  featureChanges: (tenantId: string) => [...tenantKeys.all, tenantId, 'feature-changes'] as const,
}

/**
 * The caller's own organization. Keyed by tenant id, not just "mine": a super
 * admin moving between tenants in support mode must not be shown the features of
 * the one they just left.
 */
export const organizationKeys = {
  all: ['organization'] as const,
  detail: (tenantId: string) => [...organizationKeys.all, tenantId] as const,
}

export const superAdminKeys = {
  all: ['super-admins'] as const,
  lists: () => [...superAdminKeys.all, 'list'] as const,
  list: () => [...superAdminKeys.lists()] as const,
}

export const vendorKeys = {
  all: ['vendors'] as const,
  lists: () => [...vendorKeys.all, 'list'] as const,
  /** One entry per page — see the note on `venueKeys.list`. */
  list: (page?: number) => [...vendorKeys.lists(), page ?? 'all'] as const,
  users: (vendorId: string) => [...vendorKeys.all, vendorId, 'users'] as const,
  /** A vendor's people: a given vendor's (super admin) or the signed-in vendor's own. */
  people: (scope: VendorPeopleScope) =>
    scope.kind === 'own' ? ([...vendorKeys.all, 'self', 'users'] as const) : vendorKeys.users(scope.vendorId),
  venues: (vendorId: string) => [...vendorKeys.all, vendorId, 'venues'] as const,
  self: () => [...vendorKeys.all, 'self'] as const,
  /** The signed-in vendor company's own profile, for its editor. */
  profile: () => [...vendorKeys.all, 'self', 'profile'] as const,
  /** What the signed-in vendor company's profile still needs. Under `profile`, so a profile refresh refreshes it. */
  checklist: () => [...vendorKeys.all, 'self', 'profile', 'checklist'] as const,
  /** The signed-in vendor company's page on csvenueview.com. Under `profile`, since what the profile has decides whether it shows. */
  publicPage: () => [...vendorKeys.all, 'self', 'profile', 'public-page'] as const,
  /** A company's profile, as venue staff and planners read it. */
  profileOf: (vendorId: string) => [...vendorKeys.all, vendorId, 'profile'] as const,
  selfVenues: () => [...vendorKeys.all, 'self', 'venues'] as const,
  /** The signed-in vendor company's own events, across organizations. */
  selfEvents: () => [...vendorKeys.all, 'self', 'events'] as const,
  /** One of them, read-only. */
  selfEvent: (eventId: string) => [...vendorKeys.selfEvents(), eventId] as const,
  /** Venues near the signed-in vendor (discovery). */
  selfNearby: () => [...vendorKeys.all, 'self', 'nearby'] as const,
  /** A venue's vendor-service approvals (keyed by venue). */
  venueServices: (venueId: string) => [...vendorKeys.all, 'venue', venueId, 'services'] as const,
  /** A search of every company, as one venue's manager runs it. */
  search: (venueId: string, q: string, service?: VendorServiceValue) =>
    [...vendorKeys.all, 'venue', venueId, 'search', q, service ?? 'any'] as const,
  /** The venue's vendors, as the couple or planner of one of its events browses them. */
  directory: (eventId: string, query: VendorDirectoryQuery) =>
    [...vendorKeys.all, 'event', eventId, 'directory', query.q, query.service ?? 'any', query.unit ?? 'any', query.maxPrice ?? 'any'] as const,
  /** The venue's vendors, as the planner of one of its events picks from them. */
  eventChoices: (eventId: string, q: string, service?: VendorServiceValue) =>
    [...vendorKeys.all, 'event', eventId, 'choices', q, service ?? 'any'] as const,
  /** What a company stopping work would do. Asked fresh each time the warning opens. */
  removal: (target: VendorRemovalTarget) =>
    [...vendorKeys.all, 'removal', target.kind, target.vendorId, 'venueId' in target ? target.venueId : 'everywhere'] as const,
  /** A venue's pending vendor requests (keyed by venue). */
  venueRequests: (venueId: string) => [...vendorKeys.all, 'venue', venueId, 'requests'] as const,
}

/** Quote requests (docs/vendor-growth.spec.md, "Quote requests"). */
export const quoteKeys = {
  all: ['quote-requests'] as const,
  /** An event's: the couple's own, or all for its planner and the venue's staff. */
  event: (eventId: string) => [...quoteKeys.all, 'event', eventId] as const,
  /** The signed-in vendor company's, one entry per page. */
  own: (page: number) => [...quoteKeys.all, 'own', page] as const,
}

export const reviewKeys = {
  all: ['vendor-reviews'] as const,
  /** An event's companies and the reader's reviews of them. */
  event: (eventId: string) => [...reviewKeys.all, 'event', eventId] as const,
  /** What waits for FLOH Venues' admins. */
  toApprove: () => [...reviewKeys.all, 'to-approve'] as const,
  /** Every page of the signed-in vendor company's published reviews. */
  ownAll: () => [...reviewKeys.all, 'own'] as const,
  /** One page of them. */
  own: (page: number) => [...reviewKeys.ownAll(), page] as const,
}

export const vendorFurnitureKeys = {
  all: ['vendor-furniture'] as const,
  items: () => [...vendorFurnitureKeys.all, 'items'] as const,
  groups: () => [...vendorFurnitureKeys.all, 'groups'] as const,
}

export const inventoryKeys = {
  all: ['inventory'] as const,
  venue: (venueId: string) => [...inventoryKeys.all, venueId] as const,
}

/**
 * What marking an event's client as a planner would do, for a company name.
 * Not under `eventKeys`: converting refreshes every event read, and the preview
 * of someone no longer a client must not be asked for again.
 */
export const plannerConversionKeys = {
  all: ['planner-conversion'] as const,
  preview: (eventId: string, clientId: string, companyName: string) =>
    [...plannerConversionKeys.all, eventId, clientId, companyName] as const,
}

export const clientKeys = {
  all: ['clients'] as const,
  search: (q: string) => [...clientKeys.all, 'search', q] as const,
}

export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  /**
   * One entry per listing: the filters and the page are both part of what was
   * asked for, so both are part of the key. Without them, a filtered page would
   * be written over an unfiltered one and the two would swap at random.
   */
  list: (query?: object, page?: number) =>
    [...eventKeys.lists(), query ?? 'all', page ?? 'all'] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  /** The companies planning the caller's events, for the list's Planner filter. */
  planners: () => [...eventKeys.all, 'planners'] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
  gallery: (id: string) => [...eventKeys.all, 'gallery', id] as const,
  /**
   * Which plans a piece is on, asked each time its removal is confirmed. Not
   * under `detail`: the removal reloads the event, and a key nested there would
   * ask again about a piece that is gone.
   */
  removalImpact: (id: string, itemId: string) => [...eventKeys.all, 'removal-impact', id, itemId] as const,
  /** Who else has the event's plans open, asked each time a delete is confirmed. */
  editors: (id: string) => [...eventKeys.all, 'editors', id] as const,
  planRevisions: (id: string, phaseId: string) => [...eventKeys.all, 'plan-revisions', id, phaseId] as const,
  deletedPlans: (id: string) => [...eventKeys.all, 'deleted-plans', id] as const,
  /** One venue's calendar for one window — the window is part of the key so
   * paging months caches each month rather than replacing the last. */
  calendar: (venueId: string, from: string, to: string) =>
    [...eventKeys.all, 'calendar', venueId, from, to] as const,
  calendars: () => [...eventKeys.all, 'calendar'] as const,
  /**
   * Track availability for one venue and one window.
   *
   * The window is part of the key because the answer is only true for that
   * window — a lane free at 10:00 is taken at 16:00. `null` stands for "not
   * asked", which is its own cacheable answer (every lane unknown) rather than
   * a missing one.
   */
  trackAvailability: (
    venueId: string,
    date: string | null,
    startsAt: string | null,
    endsAt: string | null,
    excludeEventId: string | null,
  ) => [...eventKeys.all, 'track-availability', venueId, date, startsAt, endsAt, excludeEventId] as const,
  /** Every message sent to one event's client contacts. */
  emails: (eventId: string) => [...eventKeys.all, 'emails', eventId] as const,
  /** What clearing one venue's events would remove, for the confirmation. */
  venuePurge: (venueId: string) => [...eventKeys.all, 'venue-purge', venueId] as const,
}

/** A person's own API tokens. */
export const apiTokenKeys = {
  all: ['api-tokens'] as const,
  lists: () => [...apiTokenKeys.all, 'list'] as const,
  list: () => [...apiTokenKeys.lists()] as const,
  /** What one token changed through MCP tools. */
  changes: (id: string) => [...apiTokenKeys.all, id, 'changes'] as const,
}

/** A venue's timeline templates, and an event's own run of the day. */
export const timelineKeys = {
  templates: (venueId: string) => ['venues', venueId, 'timeline-templates'] as const,
  forEvent: (eventId: string) => ['events', eventId, 'timeline'] as const,
}

/**
 * A venue's page on the public site. What it needs (a photo, an address the map
 * finds) and its address (from the name) change with the venue, so the venue's
 * photo and update hooks invalidate it too.
 */
export const venuePageKeys = {
  settings: (venueId: string) => ['venues', venueId, 'public-page'] as const,
}

/**
 * The public schedule. Keyed by the share token, not by venue: the crew's pages
 * never learn a venue id, and two tokens for the same venue are two caches
 * because one of them is revoked.
 */
export const publicScheduleKeys = {
  link: (venueId: string) => ['venues', venueId, 'share-link'] as const,
  schedule: (token: string) => ['public-schedule', token] as const,
  event: (token: string, eventId: string) => ['public-schedule', token, 'events', eventId] as const,
}

/**
 * One OAuth authorization awaiting an answer.
 *
 * Keyed by the request id and nothing else: each is single-use, lives ten
 * minutes, and is never listed — there is no collection here to invalidate.
 */
export const authorizationKeys = {
  all: ['oauth-authorizations'] as const,
  detail: (requestId: string) => [...authorizationKeys.all, requestId] as const,
}

/** Whose account an emailed invitation or reset link is for, by its token. Never listed. */
export const linkAccountKeys = {
  all: ['link-account'] as const,
  forToken: (token: string) => [...linkAccountKeys.all, token] as const,
}


/** A venue's marketing pages, keyed by venue so one venue's edit refetches only it. */
export const marketingKeys = {
  all: ['marketing'] as const,
  forVenue: (venueId: string) => [...marketingKeys.all, 'venue', venueId] as const,
}

/** A venue's GoHighLevel page, and what each connection reads from GoHighLevel. */
/** The company's ads: its set-up, and (from the numbers phase) what each venue spent. */
export const adsKeys = {
  all: ['ads'] as const,
  setup: () => [...adsKeys.all, 'setup'] as const,
  removal: (accountId: string) => [...adsKeys.all, 'removal', accountId] as const,
  company: (period: string | null) => [...adsKeys.all, 'company', period ?? 'newest'] as const,
  venue: (venueId: string) => [...adsKeys.all, 'venue', venueId] as const,
  venuePeriod: (venueId: string, start: string, view: string) => [...adsKeys.all, 'venue', venueId, 'period', start, view] as const,
}

/** Lead stats (docs/gohighlevel-stats.spec.md): the company's settings, and each venue's settings and numbers. */
/** The company's roles (docs/custom-roles.spec.md). */
export const tenantRoleKeys = {
  all: ['tenant-roles'] as const,
  list: () => [...tenantRoleKeys.all, 'list'] as const,
  detail: (id: string) => [...tenantRoleKeys.all, 'detail', id] as const,
}

export const leadStatsKeys = {
  all: ['lead-stats'] as const,
  settings: () => [...leadStatsKeys.all, 'settings'] as const,
  venues: () => [...leadStatsKeys.all, 'venue'] as const,
  venue: (venueId: string) => [...leadStatsKeys.venues(), venueId] as const,
  venueSettings: (venueId: string) => [...leadStatsKeys.venue(venueId), 'settings'] as const,
  summary: (pipeline: string | undefined, view: string | undefined, period: string | undefined) =>
    [...leadStatsKeys.all, 'summary', pipeline ?? '', view ?? '', period ?? ''] as const,
  numbers: (venueId: string, pipeline: string | undefined, view: string | undefined, period: string | undefined) =>
    [...leadStatsKeys.venue(venueId), 'numbers', pipeline ?? '', view ?? '', period ?? ''] as const,
}

export const salesKeys = {
  all: ['sales'] as const,
  venue: (venueId: string) => [...salesKeys.all, 'venue', venueId] as const,
  priceList: (venueId: string) => [...salesKeys.venue(venueId), 'price-list'] as const,
  templates: (venueId: string) => [...salesKeys.venue(venueId), 'templates'] as const,
  schedules: (venueId: string) => [...salesKeys.venue(venueId), 'schedules'] as const,
  settings: (venueId: string) => [...salesKeys.venue(venueId), 'settings'] as const,
  readiness: (venueId: string) => [...salesKeys.venue(venueId), 'readiness'] as const,
  venueBusiness: (venueId: string) => [...salesKeys.venue(venueId), 'business'] as const,
  businesses: () => [...salesKeys.all, 'businesses'] as const,
  event: (eventId: string) => [...salesKeys.all, 'event', eventId] as const,
  booking: (eventId: string) => [...salesKeys.all, 'booking', eventId] as const,
  pay: (token: string) => [...salesKeys.all, 'pay', token] as const,
}

/** Where each venue's couples come from (docs/ad-attribution.spec.md): its listing sites and lead source names. */
export const sourcesKeys = {
  all: ['sources'] as const,
  venue: (venueId: string) => [...sourcesKeys.all, 'venue', venueId] as const,
}

export const honeybookKeys = {
  all: ['honeybook'] as const,
  venue: (venueId: string) => [...honeybookKeys.all, 'venue', venueId] as const,
  status: (venueId: string) => [...honeybookKeys.all, 'status', venueId] as const,
  review: (venueId: string) => [...honeybookKeys.all, 'review', venueId] as const,
  /** Read again after each read of HoneyBook, which may have matched more events. */
  reviewAfter: (venueId: string, readAt: string | null) => [...honeybookKeys.review(venueId), readAt ?? 'unread'] as const,
}

/** What tells one pipeline's numbers from another's. */
interface PipelineKeyQuery {
  split?: string | undefined
  view?: string | undefined
  period?: string | undefined
  span?: string | undefined
  compare?: string | undefined
}

export const pipelineKeys = {
  all: ['pipeline'] as const,
  venue: (venueId: string) => [...pipelineKeys.all, 'venue', venueId] as const,
  /** The weeks a pipeline shows, as a key's parts: its split, view, newest period, span and comparison. */
  weeks: (q: PipelineKeyQuery) =>
    [q.split ?? '', q.view ?? '', q.period ?? '', q.span ?? '', q.compare ?? ''] as const,
  numbers: (venueId: string, q: PipelineKeyQuery) =>
    [...pipelineKeys.venue(venueId), 'numbers', ...pipelineKeys.weeks(q)] as const,
  couples: (venueId: string, q: PipelineKeyQuery, target: string, page: number) =>
    [...pipelineKeys.venue(venueId), 'couples', ...pipelineKeys.weeks(q), target, page] as const,
  company: (q: PipelineKeyQuery) =>
    [...pipelineKeys.all, 'company', ...pipelineKeys.weeks(q)] as const,
}

export const ghlKeys = {
  all: ['gohighlevel'] as const,
  company: () => [...ghlKeys.all, 'company'] as const,
  venues: () => [...ghlKeys.all, 'venue'] as const,
  settings: (venueId: string) => [...ghlKeys.venues(), venueId, 'settings'] as const,
  options: (connectionId: string) => [...ghlKeys.all, 'connection', connectionId, 'options'] as const,
  funnelChoices: (connectionId: string) => [...ghlKeys.all, 'connection', connectionId, 'funnel-choices'] as const,
  event: (eventId: string) => [...ghlKeys.all, 'event', eventId] as const,
  availability: (venueId: string) => [...ghlKeys.venues(), venueId, 'availability'] as const,
  couples: (venueId: string) => [...ghlKeys.venues(), venueId, 'couples'] as const,
  nameFormat: (venueId: string) => [...ghlKeys.venues(), venueId, 'name-format'] as const,
  runs: (venueId: string) => [...ghlKeys.venues(), venueId, 'runs'] as const,
  run: (venueId: string, runId: string) => [...ghlKeys.venues(), venueId, 'runs', runId] as const,
  review: (venueId: string) => [...ghlKeys.venues(), venueId, 'review'] as const,
}

export const coupleFeedbackKeys = {
  all: ['couple-feedback'] as const,
  queue: () => [...coupleFeedbackKeys.all, 'queue'] as const,
  forEvent: (eventId: string) => [...coupleFeedbackKeys.all, 'event', eventId] as const,
  detail: (id: string) => [...coupleFeedbackKeys.all, 'detail', id] as const,
  page: (eventId: string) => [...coupleFeedbackKeys.all, 'page', eventId] as const,
  links: (venueId: string) => [...coupleFeedbackKeys.all, 'links', venueId] as const,
  suggestions: (venueId: string) => [...coupleFeedbackKeys.all, 'suggestions', venueId] as const,
}
